import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ArrowRight, Lock, Mail, User as UserIcon, Eye, EyeOff, AlertCircle, ShoppingBag, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { bookData } from './bookData';
import { createCheckoutSession, STRIPE_PUBLISHABLE_KEY } from '../../../utils/stripe';
import { saveCheckoutShippingAddressSnapshot } from '../../../utils/firebase';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutAddressSnapshot, CheckoutLineItemInput, CheckoutShippingAddress } from '../../../utils/stripe';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const CHECKOUT_ADDRESS_SNAPSHOT_KEY = 'sn_checkout_shipping_address';
function saveCheckoutAddressSnapshot(sessionId: string, address: CheckoutShippingAddress | undefined) {
  if (!address) return;
  const snapshot: CheckoutAddressSnapshot = {
    sessionId,
    address
  };
  window.sessionStorage.setItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

// Resolve the product image for a cart item (supplement image or book cover).
function getCartItemImage(item: CartItem): string | undefined {
  const name = item.name?.trim().toLowerCase();
  const normalizedName = name?.replace(/[^a-z]/g, '');
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name) ?? shopData.find(s => {
    const catalogName = s.name.toLowerCase().replace(/[^a-z]/g, '');
    return Boolean(normalizedName && (catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName)));
  });
  return p?.image;
}
// Build a Stripe-compatible line item reference from a cart item.
// Priority: 1) lookupKey on the item, 2) stripePriceId on the item,
//           3) shopData lookup by lookupKey (supplements), 4) null.
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) return {
    lookupKey: item.lookupKey
  };
  if (item.stripePriceId) return {
    priceId: item.stripePriceId
  };
  const supplement = shopData.find(p => p.id === item.productId);
  if (supplement?.lookupKey) return {
    lookupKey: supplement.lookupKey
  };
  return null;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Sign in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
type Mode = 'signin' | 'signup';
export function Checkout() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn,
    signUp
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [mode, setMode] = useState<Mode>('signup');
  // A return to checkout means payment was cancelled, so retain the cart and
  // discard the checkout-complete markers created before the Stripe redirect.
  useEffect(() => {
    window.sessionStorage.removeItem('sn_checkout_clear_pending');
    window.sessionStorage.removeItem('sn_inventory_checkout_session');
    window.sessionStorage.removeItem(CHECKOUT_ADDRESS_SNAPSHOT_KEY);
  }, []);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPhysicalItems = items.some(item => item.type === 'supplement');
  const shippingCost = hasPhysicalItems ? 500 : 0;
  const taxAmount = Math.round(subtotal * 0.08);
  const total = subtotal + shippingCost + taxAmount;

  // Empty cart guard
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-20 px-6 bg-[#FFFFFF]" data-id="element-2173" data-mp="171jbo9e245yt">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2174" data-mp="1h3vd3qw0h5o3">
          <div className="w-20 h-20 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2175" data-mp="1c8g1l5lh7luu">
            <ShoppingBag className="w-8 h-8 text-[#9A9A90]" data-id="element-2176" data-mp="1q0e8ravmn7od" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-2177" data-mp="1l6jqmbvo70re">
            Your cart is empty.
          </h1>
          <p className="text-sm font-medium text-[#5A5A5A] mb-10" data-id="element-2178" data-mp="1uxwfcum69at7">
            Add a supplement to begin checkout.
          </p>
          <div className="flex justify-center" data-id="element-2179" data-mp="28we1wp0cch9j">
            <Link to="/shop" className="px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2180" data-mp="28v2wylv5t3gu">
              Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-2181" data-mp="ai2kzt3q5de8" />
            </Link>
          </div>
        </div>
      </div>;
  }
  // Auth gate passed: show the payment step
  if (user && !authLoading) {
    return <PaymentStep data-id="element-2182" data-mp="2elnrm67yb1ev" />;
  }
  // While auth is resolving, show a quiet loading state
  if (authLoading) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-2183" data-mp="2jl0a6ershmbn">
        <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-2184" data-mp="2ojtrkia75xh3" />
      </div>;
  }
  // Auth gate form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          setSubmitting(false);
          return;
        }
        await signUp(email, password, name.trim(), marketingOptIn);
      } else {
        await signIn(email, password);
      }
      // Stay on /checkout — user is now signed in, the if(user) branch above renders
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2185" data-mp="14ljkgqx6zrsj">
      <section className="pt-32 pb-8 px-6" data-id="element-2186" data-mp="10lsw0tv51nr6">
        <div className="max-w-5xl mx-auto" data-id="element-2187" data-mp="10o5gpesa6c7x">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2188" data-mp="vm6a7w18uz6r">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-2" data-id="element-2189" data-mp="1oc54q6y7joit">
              {mode === 'signup' ? 'Create an account to continue.' : 'Sign in to continue.'}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-2190" data-mp="1ek94oqck9oby">
              An account lets you track orders, access digital downloads, and
              save info for next time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-6" data-id="element-2191" data-mp="22ad8oytnvq3h">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12" data-id="element-2192" data-mp="229bj7pbwf128">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-3" data-id="element-2193" data-mp="1am0i0gcel3no">
            {/* Mode toggle */}
            <div className="flex bg-white border border-[#E5E5E5] rounded-md p-1 mb-8 shadow-sm" data-id="element-2194" data-mp="15oh2ld3yyku5">
              <button type="button" onClick={() => {
              setMode('signup');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signup' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2195" data-mp="1tekskho8im2o">
                New Customer
              </button>
              <button type="button" onClick={() => {
              setMode('signin');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signin' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-2196" data-mp="1pb6dbt84e9qa">
                Returning Customer
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2197" data-mp="1dx6cvo8qxh5">
              {mode === 'signup' && <div data-id="element-2198" data-mp="fezt7an28bt4">
                  <label htmlFor="name" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2199" data-mp="fdf1dss4r6yy">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-2200" data-mp="3iv6xnjdaud2y">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2201" data-mp="3iw8antdvl9nb" />
                    <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-2202" data-mp="3dxyebfgf0304" />
                  </div>
                </div>}

              <div data-id="element-2203" data-mp="2v4uj3pnjti3e">
                <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2204" data-mp="2v5ciuw9f6eg2">
                  Email
                </label>
                <div className="relative" data-id="element-2205" data-mp="324uk0leb3b5y">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2206" data-mp="325w4thl4jc0v" />
                  <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-2207" data-mp="2x3x2mb14el5i" />
                </div>
              </div>

              <div data-id="element-2208" data-mp="2eg1ub6oa1zjs">
                <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2209" data-mp="2efiafkf2h134">
                  Password
                </label>
                <div className="relative" data-id="element-2210" data-mp="24gkp0qcxtnoc">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-2211" data-mp="24gb0z8bgdqla" />
                  <input id="password" type={showPassword ? 'text' : 'password'} required minLength={mode === 'signup' ? 6 : undefined} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} data-id="element-2212" data-mp="20fr3g3mywkym" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle-button absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-[#5A5A5A] transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2213" data-mp="3sfd4utg588p8">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2214" data-mp="az58vvvj4ccl" /> : <Eye className="w-4 h-4" data-id="element-2215" data-mp="b06ngabr7gz2" />}
                  </button>
                </div>
                {mode === 'signin' && <div className="mt-3 text-right" data-id="element-2216" data-mp="39m9fvldvek0y">
                    <Link to="/forgot-password" className="text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-2217" data-mp="3elvw83ozwgdz">
                      Forgot password?
                    </Link>
                  </div>}
              </div>

              {mode === 'signup' && <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-2218" data-mp="3gc10n55eb06p">
                  <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-2219" data-mp="3ge55pqf1658k" />
                  <span className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2220" data-mp="2nnwsmczcqb0u">
                    Send me updates on new books, supplements, and articles.
                  </span>
                </label>}

              {error && <div className="bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2221" data-mp="2xjq5jxgzg6j5">
                  <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2222" data-mp="2sleszezazhx8" />
                  <p className="text-sm text-burgundy" data-id="element-2223" data-mp="cu0oddst48na">{error}</p>
                </div>}

              <button type="submit" disabled={submitting} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 mt-4" data-id="element-2224" data-mp="7vfqvm4xw32j">
                {submitting ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2225" data-mp="1gv0f108lze8u" />
                    {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                  </> : <>
                    {mode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
                    <ArrowRight className="w-4 h-4" data-id="element-2226" data-mp="2dlstjtfw8y8i" />
                  </>}
              </button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-2227" data-mp="24q3dlzx7sw2i">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2228" data-mp="2x7xilnrd498s" />
          </motion.aside>
        </div>
      </section>
    </div>;
}
function PaymentStep() {
  const navigate = useNavigate();
  const {
    user,
    profile,
    updateProfile
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const savedAddresses = profile?.addresses ?? [];
  const defaultAddress = savedAddresses.find(a => a.isDefault) ?? savedAddresses[0];
  const [selectedAddressId, setSelectedAddressId] = useState<string>(defaultAddress ? defaultAddress.id : 'new');
  // Re-sync the selection once saved addresses load. When the user signs in
  // directly on /checkout, the profile (and its addresses) arrives AFTER this
  // component mounts, so the initial state falls back to 'new'. Whenever a
  // default address becomes available but the current selection is 'new' or no
  // longer matches a saved address, snap to the default so the saved address is
  // pre-selected instead of showing the blank manual form.
  useEffect(() => {
    if (!defaultAddress) return;
    const selectionIsValid = savedAddresses.some(a => a.id === selectedAddressId);
    if (!selectionIsValid) {
      setSelectedAddressId(defaultAddress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses.length, defaultAddress?.id]);
  // Shipping Address Form State
  const [recipientName, setRecipientName] = useState(user?.displayName ?? '');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [newDeliveryInstructions, setNewDeliveryInstructions] = useState('');
  // Whether the address picker is expanded to show all saved addresses.
  const [expanded, setExpanded] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const selectedAddr = savedAddresses.find(a => a.id === selectedAddressId);
  const hasPhysicalItems = items.some(item => item.type === 'supplement');
  const unbuyable = items.filter(i => !getCheckoutRefFor(i));
  const currentCountry = selectedAddressId === 'new' ? country : selectedAddr?.country || 'United States';
  const currentState = selectedAddressId === 'new' ? stateProvince : selectedAddr?.state || '';
  let shippingCost = 0;
  if (hasPhysicalItems) {
    const isUS = ['united states', 'us', 'usa'].includes(currentCountry.trim().toLowerCase());
    const stateLower = currentState.trim().toLowerCase();
    const isTier1 = ['alaska', 'ak', 'hawaii', 'hi'].includes(stateLower);
    const isTier2 = ['arizona', 'az', 'california', 'ca', 'idaho', 'id', 'nevada', 'nv', 'oregon', 'or', 'utah', 'ut', 'washington', 'wa'].includes(stateLower);
    if (!isUS) {
      shippingCost = 1500; // $15.00 International
    } else if (isTier1) {
      shippingCost = 1200; // $12.00 AK/HI
    } else if (isTier2) {
      shippingCost = 800; // $8.00 Western states
    } else {
      shippingCost = 600; // $6.00 Standard US
    }
  }
  const taxAmount = 0;
  const total = subtotal + shippingCost + taxAmount;
  // Live checkout — creates a Stripe Checkout Session and redirects the
  // customer to Stripe to complete payment. On success Stripe returns the
  // customer to /account?order=success.
  const handleCheckout = async () => {
    setError(null);
    setSubmitting(true);
    console.log('CHECKOUT CART ITEMS:', items);
    try {
      const lineItems = items.map(i => {
        const ref = getCheckoutRefFor(i);
        if (!ref) return null;
        return {
          ...ref,
          quantity: i.quantity
        };
      }).filter((x): x is NonNullable<typeof x> => x !== null);
      if (lineItems.length === 0) {
        setError('No items in your cart are available for purchase yet.');
        setSubmitting(false);
        return;
      }
      if (hasPhysicalItems && !selectedAddr) {
        setError('Please save and select a shipping address before checking out.');
        setSubmitting(false);
        return;
      }
      const shippingAddress: CheckoutShippingAddress | undefined = selectedAddr ? {
        recipientName: selectedAddr.recipientName,
        line1: selectedAddr.line1,
        ...(selectedAddr.line2 ? {
          line2: selectedAddr.line2
        } : {}),
        city: selectedAddr.city,
        state: selectedAddr.state,
        postalCode: selectedAddr.postalCode,
        country: selectedAddr.country,
        ...(selectedAddr.deliveryInstructions?.trim() ? {
          deliveryInstructions: selectedAddr.deliveryInstructions.trim()
        } : {})
      } : undefined;
      const {
        clientSecret,
        sessionId
      } = await createCheckoutSession({
        lineItems,
        shippingAddress
      });
      // Keep an immutable, session-keyed copy for fulfillment. This is
      // deliberately separate from the mutable address book and browser-only
      // return snapshot so admins can always resolve the address selected here.
      // A missing snapshot permission must never prevent a customer from paying.
      try {
        await saveCheckoutShippingAddressSnapshot(sessionId, shippingAddress);
      } catch (snapshotError) {
        console.error('[Checkout] Could not save fulfillment snapshot:', snapshotError);
      }
      // Stripe unloads this app for payment. Preserve the session identifier
      // so the return page can apply this paid order's inventory adjustment
      // once, then mark the cart as ready to clear.
      window.sessionStorage.setItem('sn_inventory_checkout_session', sessionId);
      saveCheckoutAddressSnapshot(sessionId, shippingAddress);
      setClientSecret(clientSecret);
      setSubmitting(false);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong starting checkout.');
      setSubmitting(false);
    }
  };
  // Save a brand-new address to the profile right here on the checkout
  // screen, then select it and collapse the picker.
  const handleSaveNewAddress = async () => {
    if (!recipientName.trim() || !addressLine1.trim() || !city.trim() || !stateProvince.trim() || !zip.trim() || !country.trim()) {
      setError('Please fill out all required shipping address fields.');
      return;
    }
    setSavingAddr(true);
    setError(null);
    try {
      const newAddress = {
        id: Math.random().toString(36).substring(2, 9),
        recipientName: recipientName.trim(),
        line1: addressLine1.trim(),
        line2: addressLine2.trim(),
        city: city.trim(),
        state: stateProvince.trim(),
        postalCode: zip.trim(),
        country: country.trim(),
        deliveryInstructions: newDeliveryInstructions.trim(),
        isDefault: savedAddresses.length === 0
      };
      await updateProfile({
        addresses: [...savedAddresses, newAddress]
      });
      setSelectedAddressId(newAddress.id);
      setExpanded(false);
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setStateProvince('');
      setZip('');
      setNewDeliveryInstructions('');
    } catch (err: any) {
      setError(err?.message ?? 'Could not save this address. Please try again.');
    } finally {
      setSavingAddr(false);
    }
  };
  if (clientSecret) {
    return <div className="min-h-screen flex flex-col lg:flex-row font-sans" data-id="element-2229" data-mp="i0drtlbaqbbj">
        {/* Left Column: Stripe Checkout */}
        <div className="w-full lg:w-1/2 bg-white pt-32 pb-24 px-6 lg:px-12 xl:px-8 flex flex-col items-center lg:items-end" data-id="element-2230" data-mp="e0btnm4rs010">
          <div className="w-full max-w-md" data-id="element-2231" data-mp="921u6zc0id9l">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{
            clientSecret
          }} data-id="element-2232" data-mp="28duwnbhkkatp">
              <EmbeddedCheckout data-id="element-2233" data-mp="23dys9b4ymhdm" />
            </EmbeddedCheckoutProvider>
          </div>
        </div>

        {/* Right Column: Order Summary */}
        <div className="w-full lg:w-1/2 pt-32 pb-24 px-6 lg:px-12 xl:px-8 flex flex-col items-center lg:items-start bg-[#FBFBFB] lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto" data-id="element-2234" data-mp="2mgqt9r0je42h">
          <div className="w-full max-w-md" data-id="element-2235" data-mp="2djqaq105f06y">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2236" data-mp="2ih0760lygc4a" />
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2237" data-mp="367vrydrzu721">
      <section className="pt-32 pb-24 px-6 bg-[#FFFFFF]" data-id="element-2238" data-mp="3a8fopg8nn8nn">
        <div className="max-w-5xl mx-auto flex flex-col gap-6" data-id="element-2239" data-mp="3a7waa9i7r2ed">
          <div className="w-full" data-id="element-2240" data-mp="2w9g36hgfv9vp">
            <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2241" data-mp="2w852iqiaii8t">
              Shipping Address
            </h2>

            <div className="bg-white border border-[#D1D1D1] shadow-sm rounded-md overflow-hidden" data-id="element-2242" data-mp="1kthmpsixllkg">
              {/* COLLAPSED — only the selected address with a subtle Change action */}
              {selectedAddressId !== 'new' && !expanded && selectedAddr && <div className="p-5" data-id="element-2243" data-mp="1i7qozxftug73">
                  <div className="flex items-start gap-4" data-id="element-2244" data-mp="1iawevewa5m3v">
                    <div className="pt-1" data-id="element-2245" data-mp="1d973fy55b6f9">
                      <span className="block w-4 h-4 rounded-full border-2 border-tealslate flex items-center justify-center" data-id="element-2246" data-mp="1dcbgll0l9ci2">
                        <span className="block w-2 h-2 rounded-full bg-tealslate" data-id="element-2247" data-mp="1ma5549s431uo" />
                      </span>
                    </div>
                    <div className="flex-1 flex items-start justify-between gap-4" data-id="element-2248" data-mp="yllxlwgvyt3n">
                      <div data-id="element-2249" data-mp="uksb97j3dmcv">
                        <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2250" data-mp="uhe9a0ecbf5y">
                          {selectedAddr.recipientName}
                          {selectedAddr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2251" data-mp="13im6ef54e9yg">
                              Default
                            </span>}
                        </p>
                        <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2252" data-mp="oi8mlmua2v53">
                          {selectedAddr.line1}
                          {selectedAddr.line2 && <br data-id="element-2253" data-mp="ti3cbn2bheok" />}
                          {selectedAddr.line2}
                          <br data-id="element-2254" data-mp="yg4r3vspmru8" />
                          {selectedAddr.city}, {selectedAddr.state}{' '}
                          {selectedAddr.postalCode}
                          <br data-id="element-2255" data-mp="138y0iwz4iocn" />
                          {selectedAddr.country}
                        </p>
                      </div>
                      <button type="button" onClick={() => setExpanded(true)} className="checkout-change-button shrink-0 text-sm font-bold text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-2256" data-mp="1c706zs1mm97b">
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Delivery instructions — read-only display of the note
                   saved to this address (editable from the Account page). */}
                  {selectedAddr.deliveryInstructions?.trim() && <div className="mt-4 pl-8" data-id="element-2257" data-mp="252hlf8cbdju5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1.5" data-id="element-2258" data-mp="254c0w74tgjqu">
                        Delivery instructions
                      </p>
                      <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2259" data-mp="1wafmxflt2dmi">
                        {selectedAddr.deliveryInstructions}
                      </p>
                    </div>}
                </div>}

              {/* EXPANDED — all saved addresses stacked, plus add-new affordance */}
              {selectedAddressId !== 'new' && expanded && <AnimatePresence initial={false} data-id="element-2260" data-mp="2ezvv8p3qfadh">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-2261" data-mp="2f2i54ve3i1yv">
                    {savedAddresses.length > 0 && <div className="divide-y divide-[#E5E5E5]" data-id="element-2262" data-mp="ym5i6sumxbrp">
                        {savedAddresses.map(addr => <label key={addr.id} className={`block p-5 cursor-pointer transition-colors ${selectedAddressId === addr.id ? 'bg-[#F9F9F9]' : 'bg-white hover:bg-[#FAFAFA]'}`} data-id="element-2263" data-mp="103b6kyga4kn1">
                            <div className="flex items-start gap-4" data-id="element-2264" data-mp="1501l3hcfr86c">
                              <div className="pt-1" data-id="element-2265" data-mp="r24mkfef8rp1">
                                <input type="radio" name="shippingAddress" value={addr.id} checked={selectedAddressId === addr.id} onChange={() => {
                          setSelectedAddressId(addr.id);
                          setExpanded(false);
                        }} className="w-4 h-4 accent-tealslate" data-id="element-2266" data-mp="w1h8mxxgxuqk" />
                              </div>
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2267" data-mp="8dznvmcmfwx3">
                                <div data-id="element-2268" data-mp="ce9qqq54a77q">
                                  <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-2269" data-mp="3ujd0io6137eh">
                                    {addr.recipientName}
                                    {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-2270" data-mp="3f4s7jtk2dvk">
                                        Default
                                      </span>}
                                  </p>
                                  <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2271" data-mp="3pw57h5yurc54">
                                    {addr.line1}
                                    {addr.line2 && <br data-id="element-2272" data-mp="2pmapac7dvdc" />}
                                    {addr.line2}
                                    <br data-id="element-2273" data-mp="3twyw61k4cuz2" />
                                    {addr.city}, {addr.state} {addr.postalCode}
                                    <br data-id="element-2274" data-mp="cew4cump3m3k" />
                                    {addr.country}
                                  </p>
                                </div>
                                {addr.deliveryInstructions?.trim() && <div className="sm:border-l sm:border-[#E5E5E5] sm:pl-4" data-id="element-2275" data-mp="lhngyxrb4nws">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1" data-id="element-2276" data-mp="hg20z3jh7tid">
                                      Delivery instructions
                                    </p>
                                    <p className="text-sm text-[#5A5A5A] leading-relaxed whitespace-pre-line" data-id="element-2277" data-mp="1sri1mh1l4dk0">
                                      {addr.deliveryInstructions}
                                    </p>
                                  </div>}
                              </div>
                            </div>
                          </label>)}
                      </div>}
                    <div className="p-5 border-t border-[#E5E5E5]" data-id="element-2278" data-mp="1a3mv9i8ay20x">
                      <button type="button" onClick={() => {
                    setRecipientName(user?.displayName ?? '');
                    setSelectedAddressId('new');
                  }} className="checkout-change-button inline-flex items-center gap-1.5 text-sm font-bold text-[#5A5A5A] hover:text-[#111111] transition-colors" data-id="element-2279" data-mp="1e4f5adp00ay9">
                        <Plus className="w-4 h-4" data-id="element-2280" data-mp="qd9qm1usnopj" />
                        Add new address
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>}

              {selectedAddressId === 'new' && <div className="p-6 md:p-8" data-id="element-2281" data-mp="du5sobpzjcil">
                  <div className="space-y-5" data-id="element-2282" data-mp="9tc2tcszfnn2">
                    <div data-id="element-2283" data-mp="9w81528qsec1">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2284" data-mp="4vk86b0ydnf6">
                        Recipient Full Name *
                      </label>
                      <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2285" data-mp="3w3odxgx0mybv" />
                    </div>
                    <div data-id="element-2286" data-mp="3dbn8fvpqk9th">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2287" data-mp="3d9sl6aqzs5m8">
                        Address Line 1 *
                      </label>
                      <input type="text" required value={addressLine1} onChange={e => setAddressLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2288" data-mp="2ha7t8aju3xzb" />
                    </div>
                    <div data-id="element-2289" data-mp="2mac9bzgtyqp0">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2290" data-mp="2ma3tbypev59l">
                        Address Line 2 (Optional)
                      </label>
                      <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2291" data-mp="1tloi7n9eh73j" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2292" data-mp="1fre03orwsb6g">
                      <div data-id="element-2293" data-mp="1at411332cqfk">
                        <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2294" data-mp="1atvrjzhszxyz">
                          City *
                        </label>
                        <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2295" data-mp="3r8h7npd2yb70" />
                      </div>
                      <div className="grid grid-cols-2 gap-5" data-id="element-2296" data-mp="93w1j915uri7">
                        <div data-id="element-2297" data-mp="e27evtqnzk56">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2298" data-mp="e0e7udc52xwn">
                            State / Province *
                          </label>
                          <input type="text" required value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2299" data-mp="50hl4h9b8948" />
                        </div>
                        <div data-id="element-2300" data-mp="va89zddn16wp">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2301" data-mp="qbwzd0fiqsdf">
                            ZIP / Postal *
                          </label>
                          <input type="text" required value={zip} onChange={e => setZip(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2302" data-mp="ljdq2fnv6jef" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-2303" data-mp="2n43f3ywckit">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2304" data-mp="2of58y8s7i7a">
                        Country *
                      </label>
                      <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2305" data-mp="1x0n7yxrch3dm" />
                    </div>
                    <div data-id="element-2306" data-mp="3ftx1n4fjesx7">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-2307" data-mp="3ft55nsi12t6e">
                        Delivery Instructions (Optional)
                      </label>
                      <textarea rows={3} value={newDeliveryInstructions} onChange={e => setNewDeliveryInstructions(e.target.value)} placeholder="e.g. Leave at the front door, gate code is 1234…" className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] placeholder:text-[#9A9A90] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2308" data-mp="3ona530b89cxj" />
                    </div>

                    <div className="flex items-center gap-3 pt-2" data-id="element-2309" data-mp="g88g9l7dhf1h">
                      <button type="button" onClick={handleSaveNewAddress} disabled={savingAddr} className="px-5 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-id="element-2310" data-mp="ga2t0c8hq0uh">
                        {savingAddr ? <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2311" data-mp="sr3twydqbjn7" />
                            Saving…
                          </> : 'Save address'}
                      </button>
                      {defaultAddress && <button type="button" onClick={() => {
                    setSelectedAddressId(defaultAddress.id);
                    setExpanded(false);
                  }} className="text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-2312" data-mp="12mxg79h92ri6">
                          Cancel
                        </button>}
                    </div>
                    <p className="text-xs text-[#9A9A90] leading-relaxed" data-id="element-2313" data-mp="1lehwbc0757ep">
                      This address will be saved to your account for future
                      orders.
                    </p>
                  </div>
                </div>}
            </div>

            {error && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2314" data-mp="250t5xeezckpo">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2315" data-mp="1r1abfof3d7vu" />
                <p className="text-sm text-burgundy" data-id="element-2316" data-mp="1r3cwrv5aucu8">{error}</p>
              </div>}
          </div>

          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-2317" data-mp="1v4yje87my5hm">
            <CartSummary shippingCost={shippingCost} taxAmount={taxAmount} total={total} data-id="element-2318" data-mp="1m2yxp2loed8c" />

            {unbuyable.length > 0 && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-2319" data-mp="17f46jqnbggxd">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-2320" data-mp="1ccxf2qc3dn0t" />
                <div className="text-sm text-burgundy" data-id="element-2321" data-mp="ydxnb9ta8s0f">
                  <p className="font-bold mb-1" data-id="element-2322" data-mp="13bpner42zkfp">
                    Some items aren't available for purchase yet:
                  </p>
                  <ul className="list-disc ml-5" data-id="element-2323" data-mp="351wvlrn33j0b">
                    {unbuyable.map(i => <li key={i.productId} data-id="element-2324" data-mp="30aeth09brcdr">{i.name}</li>)}
                  </ul>
                </div>
              </div>}
          </motion.aside>

<div className="w-full" data-id="element-2325" data-mp="2vbv8b22k24mg">
      <button type="button" onClick={handleCheckout} disabled={submitting || items.length === 0} className="w-full px-6 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 text-[18px]" data-id="element-2326" data-mp="2v8zb5fyj7jz2">
        {submitting ? <>
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2327" data-mp="3ey9qutya24x6" />
            Preparing secure payment…
          </> : <>
            Continue to Payment
          </>}
      </button>

      <p className="text-sm text-[#5A5A5A] text-center mt-4" data-id="element-2328" data-mp="23knou87bk8u8">
        Secure payment processed by Stripe
      </p>
</div>
        </div>
      </section>
    </div>;
}
function CartSummary({
  shippingCost,
  taxAmount,
  total
}: {
  shippingCost: number;
  taxAmount: number;
  total: number;
}) {
  const {
    items,
    subtotal,
    itemCount
  } = useCart();
  return <div className="w-full" data-id="element-2329" data-mp="n2x2tj6edmeo">
      <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2330" data-mp="n2ndzyx39lak">
        Order Summary
      </h2>
      <div className="bg-transparent rounded-md" data-id="element-2331" data-mp="rvq8c99owyrt">
        <div className="px-6 py-4 flex items-center justify-end bg-transparent" data-id="element-2332" data-mp="wti97ssbbv85">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-2333" data-mp="11scsevu6zlss">Price</span>
        </div>
        <ul data-id="element-2334" data-mp="11u61ehsa2i3y">
        {items.map(item => {
          const image = getCartItemImage(item);
          return <li key={item.productId} className="px-6 py-5 flex gap-4 bg-transparent" data-id="element-2335" data-mp="16pbovl1bw4u5">
              {/* Image */}
              {image && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-2336" data-mp="38j2j02baoupu">
                  <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-2337" data-mp="3ckebcfljzu1q" />
                </div>}

              {/* Details */}
              <div className="flex-1 min-w-0" data-id="element-2338" data-mp="2tn44wsn6fr4r">
                <p className="text-sm sm:text-base font-bold text-[#111111] leading-snug" data-id="element-2339" data-mp="2tlszkoe32bbj">
                  {item.name}
                </p>
                <p className="text-sm text-tealslate font-medium mt-1" data-id="element-2340" data-mp="32gpk2d397aep">
                  In Stock
                </p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-2341" data-mp="2yg5mx9iok6sk">
                  {item.type === 'book' ? 'Digital Book' : 'Supplement'} · Qty{' '}
                  {item.quantity}
                </p>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right" data-id="element-2342" data-mp="2jr10th8rlif6">
                <p className="text-sm sm:text-base font-bold text-[#111111] whitespace-nowrap" data-id="element-2343" data-mp="2jnllqef3c9dr">
                  {formatPrice(item.unitPrice * item.quantity)}
                </p>
              </div>
            </li>;
        })}
      </ul>
      <div className="px-6 py-5 text-right space-y-2 bg-transparent" data-id="element-2344" data-mp="3q6tjs9dbmq0f">
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2345" data-mp="2ho8gujzvd1s">
          <span data-id="element-2346" data-mp="2gd7f2hxojed">Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):</span>
          <span data-id="element-2347" data-mp="6k1hbru1fr2s">{formatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2348" data-mp="3op4qbnn4kn8h">
          <span data-id="element-2349" data-mp="3or7ncrmp3gmp">Shipping:</span>
          <span data-id="element-2350" data-mp="3tnfvvm6e0bkw">{shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}</span>
        </div>
        <div className="flex justify-between text-sm text-[#5A5A5A]" data-id="element-2351" data-mp="l8eeut8rxhze">
          <span data-id="element-2352" data-mp="la8wnyor0tz9">Estimated Tax:</span>
          <span data-id="element-2353" data-mp="q7qxf1sm42w6">{formatPrice(taxAmount)}</span>
        </div>
        <div className="flex justify-between text-lg font-bold text-[#111111] pt-3 border-t border-[#E5E5E5] mt-3" data-id="element-2354" data-mp="q9bwn4pvga0d">
          <span data-id="element-2355" data-mp="ghftvgcubvpt">Total:</span>
          <span data-id="element-2356" data-mp="ggnx427ibrxy">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  </div>;
}